// Manila, "Afterglow over Manila Bay": a mid-October evening on the Baywalk in Malate, twenty minutes after the sun
// has gone down behind Mount Mariveles. We stand on the promenade by the seawall (14.569 N, 120.982 E) and look west
// (about 265°) across Manila Bay. Left to right as really seen: the snack kiosk on the promenade (the warm corner),
// the bay's anchorage full of ships, Mount Mariveles' long flat-topped massif on the Bataan shore 56 km away (summit at
// 265°; the sun set just left of it, at 262°), its long right slope running down towards Limay, and on the right the
// shore curving north to the Port Area's gantry cranes (317°). Corregidor (245°) and Mount Natib (285°) lie just outside.
// A postcard, not a camera: the far shore is drawn as a long lens shows it (Mariveles about five times the size the eye
// gives it, its true shape kept), the near and middle distance compressed without changing the order.
// Back to front: the afterglow sky (indigo with stars above, violet, rose, then orange to gold behind the mountain), a
// young crescent moon low in the west, a few lit streaks of cloud -> Mariveles and the lights at its foot -> the port's
// cranes -> the bay, molten orange under the glow, with ships at anchor at three distances -> the seawall with people on
// it -> coconut palms, the Baywalk lamps, the snack kiosk with its roof sign (the billboard) -> the wet promenade.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HZ = 138;                     // the horizon: the bay meets the Bataan shore
const WALL = 190;                   // top of the seawall; the bay shows from HZ down to it
const PROM = 201;                   // the promenade at the foot of the seawall (people standing at the wall)
const FEET = 205;                   // walkers on the promenade (above the bottom 63 rows)
const MOON = { x: 186, y: 42, r: 7 };
const GLOW = { x: 156 };            // where the sun went down, a little left of the summit
// Mount Mariveles: PX is the centre of its summit plateau, MH its height above the horizon in pixels. The profile is
// traced from photographs taken across the bay from Makati (see MARI_PROFILE).
const MARI = { px: 230, mh: 32 };
const BB = { x: 40, y: 118, w: 50, h: 20 };            // the kiosk's roof sign: the billboard's face
const KIOSK = { x0: 28, x1: 110, eave: 166, base: 203 };   // the snack kiosk (cozy corner) on the promenade
const LAMPS = [{ x: 300 }, { x: 414 }];                // the Baywalk's flame-crowned lamp posts, along the seawall
const CRANES = [{ x: 432, h: 22 }, { x: 446, h: 25 }, { x: 462, h: 21 }];   // the Port Area's gantry cranes, far right

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    if (a < 255 && d[o + 3]) {                       // blend over what is there
      const f = a / 255;
      d[o] = Math.round(lerp(d[o], rgb[0], f)); d[o + 1] = Math.round(lerp(d[o + 1], rgb[1], f)); d[o + 2] = Math.round(lerp(d[o + 2], rgb[2], f));
      return;
    }
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, get, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// integer Bresenham line through a put(x, y) function
function bres(put, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    put(x0, y0, col);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], t)).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
// pick from a dark-to-light ramp with ordered dither
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}

/* ================= Sign font: FONT3 with wide M, W, N, K, V (FONT3's read as H and Π at 1x) ================= */
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y), col); cx += (g?.[0].length ?? 3) + 1; }
  return cx - x - 1;
}

/* ================= Sky: the afterglow, gold behind the mountain, rose and violet above, indigo with stars ================= */
const SKY = ['#060821', '#0a0d2b', '#101236', '#171640', '#21194a', '#2d1d53', '#3c205a', '#4e235e', '#63265f',
  '#7a2a5c', '#922f57', '#aa3a4f', '#c04a45', '#d45c3b', '#e57034', '#f2862f', '#fb9c34', '#ffb444', '#ffcb62'];
// how bright the afterglow is at (i, y): strongest on the horizon where the sun went down, spreading along it
function glowAt(i, y) {
  const d = Math.hypot((i - GLOW.x) / 440, (HZ - y) / 92);
  return Math.max(0, 1 - d);
}
function skyValue(i, y) {
  const u = clamp(y / HZ, 0, 1);
  let v = Math.pow(u, 2.2) * 0.56;                                   // night above, warming towards the horizon
  v += Math.pow(glowAt(i, y), 1.25) * 0.6;                           // the afterglow
  v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 44, (y - MOON.y) / 30)) * 0.05;   // a little light round the moon
  return v;
}
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => skyValue(Math.min(i, W), Math.min(y, HZ + 2)));
}
// a young crescent, a few days old, low in the west over the glow: in the tropics it lies on its back (lit from below)
function renderMoon() {
  const r = MOON.r, s = r * 2 + 3, p = painter(s, s), c = r + 1;
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - c, dy = y - c, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    const lit = Math.hypot(dx + 0.6, dy + 3.4) > r - 0.4;          // the dark disc is offset up: a lit bowl at the bottom
    if (lit) p.put(i, y, dd > r - 0.9 ? (dx < 0 ? '#fff0c8' : '#f4dca8') : dy > 3 ? '#fff8e4' : '#fcecc4');
    else p.put(i, y, '#2c2b52', 255);                                 // earthshine on the dark part
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(47);
  while (STARS.length < 150) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(Math.pow(r(), 1.5) * 96);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 14) continue;
    if (glowAt(x, y) > 0.35) continue;                                // none in the bright afterglow
    STARS.push({ x, y, k: 10 + Math.floor(r() * 50), ph: r(), b: (y < 45 ? 0.5 + r() * 0.45 : 0.22 + r() * 0.3) * (1 - glowAt(x, y)), big: r() < 0.22 });
  }
}
const VENUS = { x: 142, y: 70 };                                      // the evening star, below and left of the moon

/* ----- a few streaks of cloud lit from below by the afterglow (tileable, so they can drift) ----- */
const STREAKS = [
  { seed: 21, y: 34, h: 8, n: 2, len: 90, k: 3 },
  { seed: 4, y: 72, h: 12, n: 2, len: 110, k: 2 },
  { seed: 9, y: 92, h: 10, n: 3, len: 80, k: 4 },
];
function renderStreak(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < def.n; b++) {
    const cx = r() * W, len = def.len * (0.7 + r() * 0.6);
    for (let k = 0; k < 6; k++) { const f = (k - 2.5) / 2.5; blobs.push({ x: cx + f * len * 0.5, y: h * (0.5 + (r() - 0.5) * 0.3), rx: 12 + r() * 16, ry: (1.6 + r() * 1.8) * (1.15 - Math.abs(f) * 0.55) }); }
  }
  const p = painter(W, h), y0 = def.y;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) {
      let dx = x - b.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    }
    if (v <= 0.3) continue;
    // lit from below by the glow under it: the underside warm, the top violet-grey; the thin ends fade into the sky
    const g = glowAt(x, y0 + y), under = y > h * 0.5;
    const lit = under ? mixHex('#b85060', '#ffa868', clamp(g * 1.4, 0, 1)) : mixHex('#3e2858', '#94406a', clamp(g * 1.5, 0, 1));
    const tex = hash(x >> 1, y, def.seed) < 0.3 ? mixHex(lit, '#2a1a40', 0.3) : lit;     // a little texture so it reads as cloud
    p.put(x, y, tex, v > 0.55 ? 255 : Math.round(255 * (0.35 + (v - 0.3) * 2.4)));
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Mount Mariveles across the bay ================= */
// The Mariveles massif seen from Manila: a broad summit plateau (the old caldera's rim seen side-on) with a small notch
// at its left end, steep upper flanks on both sides, and long lower slopes running out to the Bataan shore: to the left
// towards Mariveles town and Corregidor, to the right in a shoulder at about a third of its height (Mount Samat, 274°)
// on towards Limay. Traced from a photograph taken across the bay from Makati, on nearly the same bearing as the Baywalk
// (Commons: "Mount Mariveles across Manila Bay"); in heights of the summit (MH) and offsets from the plateau's centre
// (in MH). Real ratios (photo -> drawn): width at half height / height 6.3; plateau (top 13 %) width / height 2.9 (2.8
// in a second, closer photo of the same view); the plateau's centre at 0.53 of the half-height width from its left end.
const MARI_PROFILE = [[-9.5, 0], [-8.5, 0.12], [-7.3, 0.24], [-6.5, 0.29], [-5.7, 0.31], [-4.9, 0.36], [-4.15, 0.42],
  [-3.4, 0.5], [-2.6, 0.6], [-2, 0.71], [-1.7, 0.79], [-1.45, 0.88], [-1.3, 0.9], [-1.18, 0.86], [-1, 0.95], [-0.6, 0.97],
  [-0.25, 0.99], [0.1, 0.96], [0.55, 1], [0.9, 0.98], [1.3, 0.91], [1.7, 0.8], [2.1, 0.69], [2.5, 0.6], [2.9, 0.52],
  [3.65, 0.41], [4.45, 0.34], [5.2, 0.29], [6, 0.29], [6.9, 0.33], [7.6, 0.27], [8.6, 0.18], [9.6, 0.1], [10.5, 0.03], [11, 0]];
// height of the ridge above the horizon (px, fractional) at column x
function mariHeight(x) {
  const u = (x - MARI.px) / MARI.mh, P = MARI_PROFILE;
  if (u <= P[0][0] || u >= P[P.length - 1][0]) return 0;
  let k = 1; while (P[k][0] < u) k++;
  const [a, fa] = P[k - 1], [b, fb] = P[k], f = (u - a) / (b - a);
  const s = f * f * (3 - 2 * f);                                       // eased between the traced points
  let h = lerp(fa, fb, lerp(f, s, 0.5)) * MARI.mh;
  h += (hash(x, 3, 71) - 0.5) * 0.9 * Math.min(1, h / 8);             // the ridgeline's small roughness
  return Math.max(0, h);
}
const MARI_TOP = x => HZ - mariHeight(x);
const MC = { rim: '#ff9a52', rimD: '#d86a4a', top: '#2a1834', mid: '#311e3a', low: '#3c2642', haze: '#4e2e48', vein: '#251530' };
function renderMariveles() {
  const p = painter(W, H), put = p.put;
  for (let x = 0; x < W; x++) {
    const h = mariHeight(x);
    if (h < 0.5) continue;
    const top = Math.round(HZ - h);
    // ridges running down the flanks: a few dark gullies, offset by column
    const gully = hash(x >> 1, 9, 72) < 0.1 && h > 8, gEnd = 0.2 + hash(x >> 1, 9, 73) * 0.3;
    for (let y = top; y < HZ; y++) {
      const d = (y - top) / Math.max(1, h), hz = 1 - (HZ - y) / 6;       // depth below the ridge; haze near the horizon
      let col = d + (bayer(x, y) - 0.5) * 0.12 < 0.4 ? MC.top : d < 0.75 ? MC.mid : MC.low;
      if (hz > 0) col = hz + (bayer(x, y) - 0.5) * 0.3 > 0.7 ? MC.haze : MC.low;
      else if (gully && d > 0.08 && d < gEnd && (y - top) % 3 !== 2) col = d < 0.4 ? MC.vein : MC.top;
      put(x, y, col);
    }
    // the glow catches the ridgeline: brightest close to where the sun went down
    const g = glowAt(x, top);
    if (g > 0.1) put(x, top, g > 0.42 ? MC.rim : MC.rimD);
    else put(x, top, '#4a2c4c');
  }
  // the haze line where the far shore meets the bay
  for (let x = 0; x < W; x++) if (p.get(x, HZ - 1)) put(x, HZ - 1, mixHex(MC.haze, '#ff9a5a', glowAt(x, HZ) * 0.6));
  return p.done();
}
// lights along the Bataan shore at the mountain's foot, in strings: Mariveles town and its port (left), the coast road,
// Lamao and the refinery (a dense white cluster), Limay and Orion (right)
const SHORE_LIGHTS = [];
{
  const r = rng(53);
  const town = (x0, x1, n, warm) => { for (let k = 0; k < n; k++) SHORE_LIGHTS.push({ x: Math.round(x0 + r() * (x1 - x0)), y: HZ - 1 - Math.floor(r() * 2.4), warm: r() < warm, k: 6 + Math.floor(r() * 30), ph: r() }); };
  const road = (x0, x1, gap, y) => { for (let x = x0; x < x1; x += gap) SHORE_LIGHTS.push({ x: Math.round(x), y, warm: true, k: 4 + Math.floor(r() * 12), ph: r(), dim: true }); };
  town(MARI.px - 5.4 * MARI.mh, MARI.px - 4.3 * MARI.mh, 14, 0.85);    // Mariveles
  road(MARI.px - 4.2 * MARI.mh, MARI.px + 2.5 * MARI.mh, 6, HZ - 1);    // the coast road round the mountain's foot
  town(MARI.px + 2.6 * MARI.mh, MARI.px + 3.5 * MARI.mh, 18, 0.3);     // Lamao, the refinery
  town(MARI.px + 4.4 * MARI.mh, MARI.px + 5.4 * MARI.mh, 12, 0.75);    // Limay
  road(MARI.px + 5.5 * MARI.mh, 400, 5, HZ - 1);                        // on towards Orion
}
const FLARE = { x: Math.round(MARI.px + 3.05 * MARI.mh), y: HZ - 4 };   // the refinery's flare stack

/* ================= The Port Area far right: gantry cranes over the South Harbor piers ================= */
// the Manila Yacht Club's marina at the left edge (224°): the breakwater, hulls at their berths, a forest of masts
const MASTS = [{ x: 18, h: 24 }, { x: 22, h: 19 }, { x: 26, h: 27 }, { x: 30, h: 17 }, { x: 33, h: 22 }];
const MARINA_LIGHT = { x: 36, y: HZ - 1 };
function renderPort() {
  const p = painter(W, H), put = p.put, ink = '#1c1428', lit = '#3a2a40';
  for (let x = 12; x <= 38; x++) { put(x, HZ, '#2a1a30'); put(x, HZ + 1, '#1c1226'); if (x % 4 !== 1) put(x, HZ - 1, '#e8e0e8'); }   // hulls, white
  for (const M of MASTS) {
    for (let y = HZ - 1 - M.h; y < HZ - 1; y++) put(M.x, y, '#2a2034');
    bres(put, M.x, HZ - 1 - M.h + 2, M.x + 5, HZ - 2, '#3a2c44');                 // a forestay
    put(M.x, HZ - 2 - M.h, '#fff4e0');                                             // the masthead light
  }
  // the pier and the low sheds of the Port Area, a line of sodium floodlights
  for (let x = 404; x < W; x++) for (let y = HZ - 3; y < HZ + 1; y++) put(x, y, y === HZ - 3 ? lit : ink);
  for (let x = 452; x < W; x++) for (let y = HZ - 7; y < HZ - 3; y++) put(x, y, y === HZ - 7 ? lit : ink);   // a warehouse
  for (const C of CRANES) {
    const { x, h } = C, top = HZ - 3 - h;
    // two A-frame legs, the machinery house, the boom out over the water (to the left) and the backreach
    for (let y = top + 6; y < HZ - 3; y++) { put(x - 3, y, ink); put(x + 3, y, ink); if ((y - top) % 5 === 0) for (let i = x - 3; i <= x + 3; i++) put(i, y, ink); }
    bres(put, x - 3, top + 6, x, top, ink); bres(put, x + 3, top + 6, x, top, ink);
    for (let i = x - 13; i <= x + 7; i++) { put(i, top + 6, ink); if (i % 2) put(i, top + 7, ink); }
    for (let i = x - 1; i <= x + 3; i++) for (let y = top + 7; y < top + 10; y++) put(i, y, '#2a2034');
    bres(put, x, top, x - 12, top + 6, '#2a2034'); bres(put, x, top, x + 6, top + 6, '#2a2034');
  }
  return p.done();
}

/* ================= Ships at anchor: the bay is an anchorage, dozens of them waiting for the port ================= */
// types: 'b' bulk carrier (deck cranes, accommodation block aft), 'c' container ship (stacks, bridge aft), 't' tanker
// (low, pipe gantry, block aft). dist 0 far (a dark dash and a light), 1 middle, 2 near (windows, deck lights, rigging).
const SHIPS = [
  // far, on the horizon in front of the mountain's foot
  { x: 52, wl: 141, len: 7, type: 't', dist: 0, flip: true }, { x: 84, wl: 140, len: 5, type: 'c', dist: 0 },
  { x: 136, wl: 141, len: 5, type: 'b', dist: 0 }, { x: 226, wl: 140, len: 5, type: 't', dist: 0 },
  { x: 276, wl: 141, len: 6, type: 'b', dist: 0, flip: true }, { x: 344, wl: 139, len: 5, type: 'c', dist: 0 },
  { x: 404, wl: 141, len: 6, type: 't', dist: 0, flip: true }, { x: 190, wl: 141, len: 7, type: 'c', dist: 0 },
  { x: 96, wl: 139, len: 6, type: 'c', dist: 0 }, { x: 121, wl: 141, len: 10, type: 'b', dist: 0, flip: true },
  { x: 200, wl: 139, len: 6, type: 't', dist: 0 }, { x: 214, wl: 141, len: 8, type: 'c', dist: 0, flip: true },
  { x: 262, wl: 140, len: 7, type: 'b', dist: 0 }, { x: 290, wl: 139, len: 5, type: 't', dist: 0 },
  { x: 334, wl: 141, len: 9, type: 'b', dist: 0, flip: true }, { x: 358, wl: 140, len: 6, type: 'c', dist: 0 },
  { x: 392, wl: 139, len: 7, type: 'b', dist: 0 }, { x: 70, wl: 140, len: 6, type: 'b', dist: 0, flip: true },
  { x: 150, wl: 140, len: 8, type: 't', dist: 0 }, { x: 178, wl: 139, len: 6, type: 'b', dist: 0, flip: true },
  { x: 240, wl: 139, len: 6, type: 'c', dist: 0, flip: true }, { x: 312, wl: 140, len: 8, type: 'b', dist: 0 },
  { x: 372, wl: 139, len: 5, type: 't', dist: 0, flip: true }, { x: 420, wl: 141, len: 8, type: 'c', dist: 0 },
  // middle distance
  { x: 64, wl: 147, len: 19, type: 'c', dist: 1 }, { x: 138, wl: 145, len: 15, type: 'b', dist: 1, flip: true },
  { x: 236, wl: 148, len: 23, type: 'b', dist: 1 }, { x: 316, wl: 146, len: 16, type: 't', dist: 1, flip: true },
  { x: 380, wl: 151, len: 25, type: 'c', dist: 1, flip: true }, { x: 104, wl: 149, len: 17, type: 't', dist: 1 },
  { x: 274, wl: 144, len: 13, type: 'c', dist: 1, flip: true }, { x: 440, wl: 145, len: 14, type: 'b', dist: 1 },
  // near: a container ship in the glitter under the glow, a big bulk carrier off the seawall
  { x: 172, wl: 161, len: 44, type: 'c', dist: 2 }, { x: 296, wl: 171, len: 72, type: 'b', dist: 2, flip: true },
];
const BOX_COLS = ['#6a2a2a', '#2a3a5a', '#5a4a3a', '#3a4a4a', '#7a4a2a', '#4a2a4a'];
function renderShip(S) {
  const { len, type, dist } = S, hh = Math.max(1, Math.round(len * (type === 't' ? 0.075 : 0.085)));
  const tall = Math.round(len * (type === 'b' ? 0.2 : type === 'c' ? 0.22 : 0.17)), w = len + 4, h = hh + tall + 6, wl = h - 1;
  const p = painter(w, h), put = p.put, lights = [];
  const hullC = dist === 0 ? '#3a2240' : dist === 1 ? '#24162e' : '#170f22', topC = dist === 0 ? '#44284a' : dist === 1 ? '#2e1e38' : '#22182e';
  const lightC = dist === 2 ? '#3a2e48' : topC;
  const X = i => S.flip ? w - 1 - i : i;                               // the bow is at the right unless flipped
  // hull: the sheer rises to the bow, a raked stem, a square stern
  for (let i = 0; i < len; i++) {
    const u = i / (len - 1), rise = u > 0.85 ? Math.round((u - 0.85) / 0.15 * Math.max(1, hh * 0.5)) : 0;
    const t0 = wl - hh - rise + 1, keelIn = u > 0.94 ? Math.round((u - 0.94) / 0.06 * hh * 0.8) : 0;
    for (let y = t0; y <= wl - keelIn; y++) put(X(2 + i), y, y === t0 && dist === 2 ? lightC : hullC);
    if (dist === 2 && hh >= 4 && i % 2 === 0) put(X(2 + i), wl - 1, '#2a1428');          // the red anti-fouling at the waterline
  }
  const deck = wl - hh;
  const block = (x0, bw, bh, win) => {                                // the accommodation block / bridge
    for (let y = deck - bh + 1; y <= deck; y++) for (let i = x0; i < x0 + bw; i++) put(X(i), y, (i === x0 && dist === 2) ? lightC : topC);
    if (dist === 2) for (let i = x0 - 1; i <= x0 + bw; i++) put(X(i), deck - bh + 1, '#3a3050');        // bridge wings
    if (win && dist >= 1) for (let y = deck - bh + 3; y < deck - 1; y += 2) for (let i = x0 + 1; i < x0 + bw - 1; i++)
      if (hash(i, y, len) < (dist === 2 ? 0.45 : 0.3)) { put(X(i), y, hash(i, y, 7) < 0.7 ? '#ffd88a' : '#fff2c8'); }
    return deck - bh + 1;
  };
  let mastTop;
  if (type === 'b') {
    const top = block(3, Math.max(2, Math.round(len * 0.13)), tall - (dist === 2 ? 3 : 1), true);
    put(X(4), top - 1, topC); if (dist === 2) { put(X(5), top - 1, topC); put(X(5), top - 2, topC); }   // funnel
    const n = dist === 0 ? 0 : 4;                                      // deck cranes between the hatches
    for (let k = 0; k < n; k++) {
      const cx = 3 + Math.round(len * (0.32 + k * 0.15)), ch = Math.max(2, Math.round(len * 0.1));
      for (let y = deck - ch; y < deck; y++) put(X(cx), y, topC);
      if (dist === 2) bres((i, y) => put(X(i), y, topC), cx, deck - ch, cx + 5, deck - ch + 3, topC);
      if (dist === 2) for (let i = cx - 3; i < cx + 4; i++) if (i !== cx) put(X(i), deck, '#2e2238');   // hatch covers
    }
    mastTop = deck - Math.max(2, Math.round(len * 0.08));
  } else if (type === 'c') {
    const bx = Math.round(len * 0.74);
    for (let i = 4; i < len - 2; i++) {
      if (Math.abs(i - bx) < Math.round(len * 0.06) + 1) continue;
      const stack = Math.max(1, Math.round(len * (i < bx ? 0.07 : 0.05)) - (i > len - 6 ? 1 : 0));
      for (let y = deck - stack + 1; y <= deck; y++) put(X(i), y, dist === 2 ? mixHex(BOX_COLS[Math.floor(i / 3) % BOX_COLS.length], '#160e20', 0.45 + (y % 2) * 0.1) : topC);
    }
    block(bx - Math.round(len * 0.05), Math.max(2, Math.round(len * 0.1)), tall - 1, true);
    mastTop = deck - Math.round(len * 0.1);
  } else {
    const top = block(3, Math.max(2, Math.round(len * 0.14)), tall - 1, true);
    put(X(4), top - 1, topC);
    if (dist >= 1) for (let i = 8; i < len - 4; i++) if (i % 3) put(X(i), deck - 1, topC);   // the pipe gantry
    mastTop = deck - Math.round(len * 0.12);
  }
  // the foremast with its anchor light, and the stern light
  const fx = len - Math.max(2, Math.round(len * 0.08));
  for (let y = mastTop; y < deck; y++) put(X(fx), y, topC);
  lights.push([X(fx), mastTop - 1, '#fff6e0', 1]);
  lights.push([X(3), deck - Math.round(tall * 0.9) - 1, '#fff2d0', 0.8]);
  if (dist === 2) for (let k = 0; k < 4; k++) lights.push([X(Math.round(len * (0.3 + k * 0.15))), deck - 1, '#ffd890', 0.6]);   // deck floodlights
  return { c: p.done(), ox: Math.round(S.x - w / 2), oy: S.wl - wl, w, h, wl, lights: lights.map(([x, y, col, a]) => ({ x: Math.round(S.x - w / 2) + x, y: S.wl - wl + y, col, a })) };
}

/* ================= Manila Bay: the afterglow mirrored, molten under the glow, violet towards the seawall ================= */
const WATER = ['#0c0a22', '#120e2c', '#1a1236', '#231640', '#2e1a48', '#3c1e4e', '#4e2252', '#622652', '#7a2c50',
  '#943450', '#ae3e4a', '#c64c42', '#da5e3a', '#ea7434', '#f68e34', '#ffaa40'];
function renderWater() {
  return ditherFill(W, WALL - HZ + 2, WATER, (i, y) => {
    const d = y / (WALL - HZ);                                          // 0 at the horizon, 1 at the seawall
    const mirror = skyValue(i, HZ - 1 - y * 1.6);                       // the sky it mirrors
    return mirror * (0.98 - d * 0.4) + Math.pow(glowAt(i, HZ), 2) * 0.16 * (1 - d * 0.7) - 0.04;
  });
}

/* ================= The seawall and the Baywalk's promenade ================= */
// a low concrete wall people sit on, its coping catching the sky; the promenade of red interlocking pavers in front,
// still wet from an evening shower, lamplit round the kiosk and the lamp posts
const POOLS = [{ x: 74, y: 206, rx: 70, ry: 18, a: 1 }, { x: LAMPS[0].x, y: 210, rx: 40, ry: 14, a: 0.7 }, { x: LAMPS[1].x, y: 210, rx: 40, ry: 14, a: 0.7 }];
const poolAt = (x, y) => POOLS.reduce((m, P) => Math.max(m, Math.max(0, 1 - Math.hypot((x - P.x) / P.rx, (y - P.y) / P.ry)) * P.a), 0);
function renderWall() {
  const p = painter(W, H), put = p.put;
  for (let x = 0; x < W; x++) {
    const g = glowAt(x, HZ);
    put(x, WALL, mixHex('#9a6a6a', '#f0a070', g * 0.8));                // the coping's top, catching the afterglow
    put(x, WALL + 1, mixHex('#5a3a4a', '#8a5050', g * 0.5));
    for (let y = WALL + 2; y < PROM; y++) {
      const w = poolAt(x, y + 6), joint = x % 23 === 0, v = (y - WALL) / (PROM - WALL) * 0.5 + (bayer(x, y) - 0.5) * 0.25 - w * 0.6;
      put(x, y, joint ? '#1a1226' : v < 0.05 ? '#6a4a48' : v < 0.3 ? '#3e2c3e' : v < 0.5 ? '#33263a' : '#2a2034');
    }
    put(x, PROM, '#140e1e');
  }
  return p.done();
}
// puddles left by the evening shower: they mirror the dark sky over the viewer (a star or two) and the lamps
const PUDDLES = [{ x: 196, y: 226, rx: 46, ry: 6 }, { x: 330, y: 240, rx: 60, ry: 8 }, { x: 96, y: 252, rx: 54, ry: 9 }, { x: 420, y: 216, rx: 30, ry: 4 }];
const puddleAt = (x, y) => PUDDLES.some(P => {
  const e = ((x - P.x) / P.rx) ** 2 + ((y - P.y) / P.ry) ** 2;
  return e < 1 - 0.35 * hash(x >> 2, y, P.x);
});
function renderPromenade() {
  const p = painter(W, H), put = p.put;
  for (let y = PROM + 1, row = 0; y < H; row++) {
    const sh = 2 + Math.min(5, Math.floor(row / 2.2)), sw = 6 + Math.min(12, Math.floor(row * 1.1)), off = (row & 1) * (sw >> 1);
    for (let yy = y; yy < Math.min(H, y + sh); yy++) for (let x = 0; x < W; x++) {
      const lx = (x + off) % sw, ly = yy - y, n = hash(Math.floor((x + off) / sw), row, 31), w = poolAt(x, yy);
      const joint = lx === 0 || ly === 0;
      // wet pavers, dark violet with the sky's sheen, warm in the lamplit pools; puddles mirror the sky over us
      if (puddleAt(x, yy)) {
        const sky = skyValue(x, clamp(2 * HZ - yy - 40, 0, HZ));
        put(x, yy, w > 0.35 ? ramp(['#4a2a3a', '#8a5040', '#c88050'], w, x, yy) : ramp(['#0e0c26', '#161236', '#22184a', '#2e1c52'], sky * 1.6 + 0.1, x, yy));
        continue;
      }
      const sheen = 0.25 + 0.2 * Math.max(0, 1 - (yy - PROM) / 40);
      let v = (n - 0.5) * 0.22 + w * 0.9 + sheen * 0.4 + (bayer(x, yy) - 0.5) * 0.16;
      if (!joint && ly === 1) v += 0.1;
      const col = joint ? (w > 0.4 ? '#3a2228' : '#1c1428') : v > 0.95 ? '#8a4a3c' : v > 0.68 ? '#62363a' : v > 0.42 ? '#422838' : v > 0.18 ? '#30203a' : '#241a32';
      put(x, yy, col);
    }
    y += sh;
  }
  for (const P of PUDDLES) for (let k = 0; k < 3; k++) {               // a star or two in each puddle
    const sx = Math.round(P.x + (hash(k, P.x, 5) - 0.5) * P.rx * 1.2), sy = Math.round(P.y + (hash(k, P.y, 6) - 0.5) * P.ry);
    if (puddleAt(sx, sy) && poolAt(sx, sy) < 0.3) put(sx, sy, '#8a88b8');
  }
  // the near palms' round concrete planters at the bottom corners
  for (const [cx, r0] of [[10, 26], [472, 28]]) for (let y = 252; y < H; y++) for (let x = cx - r0; x <= cx + r0; x++) {
    const u = (x - (cx - r0)) / (2 * r0), top = 252 + Math.round(Math.abs(u - 0.5) * 6);
    if (y < top) continue;
    put(x, y, y === top ? '#6a5a62' : y === top + 1 ? '#3a2e3a' : u < 0.2 ? '#4a3c48' : u > 0.8 ? '#1e1626' : '#2e2434');
  }
  return p.done();
}

/* ================= Coconut palms ================= */
// a curved trunk with leaf scars, backlit by the glow with a warm rim, and a crown of drooping, feathery fronds
const PALMS = [
  { id: 'nearL', bx: 10, by: 262, tx: 22, ty: 26, cx: 2, w0: 8, w1: 5, n: 14, L: 50, seed: 3, near: true },
  { id: 'wallL', bx: 104, by: PROM, tx: 92, ty: 64, cx: 102, w0: 4, w1: 3, n: 10, L: 30, seed: 7 },   // behind the kiosk
  { id: 'wallR', bx: 366, by: PROM, tx: 380, ty: 64, cx: 364, w0: 4, w1: 3, n: 10, L: 32, seed: 11 },
  { id: 'nearR', bx: 472, by: 262, tx: 446, ty: 22, cx: 482, w0: 8, w1: 5, n: 15, L: 54, seed: 17, near: true },
];
function renderPalm(P, sway = 0) {
  const p = painter(W, H), put = p.put, r = rng(P.seed);
  const bez = (u) => { const a = (1 - u) * (1 - u), b = 2 * u * (1 - u), c = u * u; return [a * P.bx + b * P.cx + c * P.tx, a * P.by + b * ((P.by + P.ty) / 2) + c * P.ty]; };
  // trunk
  const steps = Math.ceil((P.by - P.ty) * 1.6);
  for (let s = 0; s <= steps; s++) {
    const u = s / steps, [cx, cy] = bez(u), hw = lerp(P.w0, P.w1, u) / 2, y = Math.round(cy);
    const ring = Math.floor((P.by - cy) / (P.near ? 4 : 3)) % 2 === 0;
    for (let i = Math.round(cx - hw); i <= Math.round(cx + hw); i++) {
      const e = (i - (cx - hw)) / (2 * hw);
      let col = e < 0.2 ? '#6a3a34' : e > 0.82 ? '#1a1018' : ring ? '#2a1c24' : '#34242a';
      if (e < 0.2 && glowAt(i, y) > 0.2) col = '#a85a40';               // the warm rim on the side towards the glow
      put(i, y, col);
    }
  }
  const [hx, hy] = bez(1);
  // fronds: a rachis drooping under its own weight, leaflets hanging from both sides
  for (let k = 0; k < P.n; k++) {
    const a = -Math.PI / 2 + (k / (P.n - 1) - 0.5) * 3.3 + (r() - 0.5) * 0.25 + sway, L = P.L * (0.75 + r() * 0.35), droop = 0.9 + r() * 0.5 + sway * 2;
    let px0 = hx, py0 = hy;
    for (let s = 1; s <= L; s++) {
      const u = s / L, x = hx + Math.cos(a) * s, y = hy + Math.sin(a) * s * 0.85 + droop * L * u * u * 0.9;
      const ix = Math.round(x), iy = Math.round(y);
      bres(put, px0, py0, ix, iy, '#0e1612');
      if (P.near && u < 0.6) bres(put, px0, py0 + 1, ix, iy + 1, '#141e18');
      px0 = ix; py0 = iy;
      if (s > 3) {                                                     // leaflets, longest mid-frond, hanging down and out
        const ll = Math.round((P.near ? 10 : 5) * Math.sin(Math.PI * Math.min(1, u * 1.15)) + 1);
        const dirx = Math.cos(a) >= 0 ? 1 : -1;
        if (s % 2 === 0 || (P.near && s % 3 === 0)) {
          bres(put, ix, iy, ix + dirx * Math.round(ll * 0.35), iy + ll, '#121c16');
          bres(put, ix, iy, ix - dirx * Math.round(ll * 0.5), iy + Math.round(ll * 0.8), '#0c140f');
        }
        if (s % 4 === 1 && glowAt(ix, iy) > 0.12) put(ix, iy - 1, '#5a3a2e');   // the glow along the top of the rachis
      }
    }
  }
  // the crown's heart and a cluster of coconuts, one dead frond hanging
  for (let j = -2; j <= 2; j++) for (let i = -3; i <= 3; i++) if (Math.abs(i) + Math.abs(j) < 4) put(hx + i, hy + j, '#141a14');
  for (const [i, j] of [[-2, 3], [0, 4], [2, 3], [1, 5], [-1, 5]]) { put(hx + i, hy + j, '#3a2a1e'); put(hx + i + 1, hy + j, '#24180f'); }
  for (let s = 0; s < (P.near ? 14 : 8); s++) put(hx + 2 + (s >> 2), hy + 2 + s, '#4a3424');
  return p.done();
}

/* ================= The Baywalk's lamp posts: a white column, a stack of lit coloured panels, a flame-shaped lamp ================= */
// as along the Malate stretch: a white column with four lit panels (red, blue, yellow, green) in its upper half, and on top
// a tulip of amber glass held by iron petals, like a torch
const LAMP_TOP = 132, PANELS = ['#ff5a5a', '#5a9aff', '#ffd84a', '#5ae08a'];
const FLAME = ['.....y.....', '....yYy....', '...yYYYy...', '..ayYYYya..', '.a.yYYYy.a.', '.a.oyYyo.a.', '.aooyyyooa.',
  '..aoooooa..', '...aaaaa...', '....bbb....', '.....b.....'];
const FLAME_KEY = { Y: '#fff4c0', y: '#ffc860', o: '#f08a3a', a: '#3a2a30', b: '#8a8290' };
function renderLamp(L) {
  const p = painter(W, H), put = p.put, x = L.x, p0 = LAMP_TOP + 13;
  for (let y = LAMP_TOP + 11; y <= PROM; y++) { put(x - 1, y, '#e8e0e8'); put(x, y, '#c0b8c8'); put(x + 1, y, '#7a7088'); }
  for (let i = x - 2; i <= x + 2; i++) { put(i, PROM, '#5a4e5a'); put(i, PROM - 1, '#8a7e88'); put(i, LAMP_TOP + 11, '#9a90a0'); }
  PANELS.forEach((col, k) => { for (let y = p0 + k * 6; y < p0 + k * 6 + 5; y++) { put(x - 1, y, mixHex(col, '#ffffff', 0.35)); put(x, y, col); put(x + 1, y, mixHex(col, '#2a1a30', 0.3)); } });
  FLAME.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = FLAME_KEY[row[i]]; if (col) put(x - 5 + i, LAMP_TOP + j, col); } });
  for (let i = x + 2; i <= x + 9; i++) put(i, p0 + 4, '#3a3040');                      // the bracket the parol hangs from
  return p.done();
}

/* ================= The snack kiosk on the promenade: the warm corner ================= */
// a little painted kiosk of the kind that sells merienda along the bay: a green corrugated roof, a red fascia, strips of
// snack sachets hanging over a lit counter with glass jars, blue plywood below; a striped tarp awning to its right over
// a fishball cart and plastic stools; capiz star lanterns (parol) hung for the Christmas season, which in the Philippines
// starts in September
const AWN = { x0: KIOSK.x1 + 2, x1: 150, y0: KIOSK.eave - 1, y1: 174 };
const CART = { x0: 118, x1: 140, top: 186, base: 203 };
const PAROLS = [{ x: 103, y: 150, col: '#ffd890' }, { x: LAMPS[0].x + 9, y: 157, col: '#ff8a7a' }, { x: LAMPS[1].x + 9, y: 157, col: '#9ad0ff' }];
const STOOLS = [{ x: 122, col: '#c83a3a' }, { x: 146, col: '#3a6ac8' }];
function renderKiosk() {
  const p = painter(W, H), put = p.put, { x0, x1, eave, base } = KIOSK;
  // the roof: corrugated sheet painted green, rust running down from the fixings, overhanging the box
  for (let y = eave - 7; y <= eave; y++) for (let x = x0 - 3; x <= x1 + 3; x++) {
    const ridge = (x & 1) === 0, rust = hash(x >> 1, 4, 41) < 0.18 && y > eave - 5;
    put(x, y, y === eave ? '#1a2a22' : y === eave - 7 ? '#5a9a72' : rust ? '#7a4a30' : ridge ? '#3e7a58' : '#2a5a40');
  }
  // the fascia
  for (let y = eave + 1; y <= eave + 8; y++) for (let x = x0; x <= x1; x++) put(x, y, y === eave + 1 || y === eave + 8 ? '#e8d8b0' : '#a82a30');
  // inside, lit: shelves of tins and bottles, strips of snack sachets hanging from the top
  for (let y = eave + 9; y < 185; y++) for (let x = x0 + 2; x <= x1 - 2; x++) {
    const v = (y - eave - 7) / 18 + (bayer(x, y) - 0.5) * 0.25 + Math.abs(x - 70) / 160;
    put(x, y, v < 0.35 ? '#ffd890' : v < 0.65 ? '#f0b060' : '#c88040');
  }
  for (const sy of [eave + 13, eave + 17]) for (let x = x0 + 4; x <= x1 - 4; x++) {
    put(x, sy, '#6a3a24');
    if (hash(x, sy, 2) < 0.7) put(x, sy - 1, ['#c83a3a', '#3a8ac8', '#e8c040', '#e8e0d0', '#4aa86a'][Math.floor(hash(x, sy, 3) * 5)]);
  }
  for (let x = x0 + 3; x <= x1 - 3; x += 3) {                        // sachets hanging in strips from the front edge
    if (Math.abs(x - 70) < 9) continue;                                // a gap over the vendor's face
    const col = ['#e84a3a', '#f0d040', '#4a9ae8', '#e87ab0', '#5ac86a'][Math.floor(hash(x, 5, 9) * 5)], n = 3 + Math.floor(hash(x, 6, 9) * 3);
    for (let k = 0; k < n - 1; k++) { put(x, eave + 9 + k * 2, col); put(x + 1, eave + 9 + k * 2, mixHex(col, '#2a1a20', 0.4)); put(x, eave + 10 + k * 2, mixHex(col, '#2a1a20', 0.25)); }
  }
  for (let y = eave + 9; y < 185; y++) { put(x0, y, '#5a2a2a'); put(x0 + 1, y, '#3a1a20'); put(x1, y, '#3a1a20'); put(x1 - 1, y, '#5a2a2a'); }
  // the counter and its glass jars
  for (let x = x0 - 1; x <= x1 + 1; x++) { put(x, 185, '#d8a868'); put(x, 186, '#8a5a34'); put(x, 187, '#3a2224'); }
  for (const [jx, col] of [[36, '#e8a040'], [44, '#c84a6a'], [86, '#e8d8a0'], [94, '#8a5a3a'], [102, '#e85a3a']]) {
    for (let y = 179; y < 185; y++) for (let x = jx; x < jx + 5; x++) {
      const glass = x === jx || x === jx + 4 || y === 179;
      put(x, y, y === 179 ? '#3a6a4a' : glass ? '#e8f0f0' : y > 181 ? col : mixHex(col, '#fff4e0', 0.5));
    }
  }
  // the blue plywood front, panelled, scuffed along the bottom
  for (let y = 188; y < base; y++) for (let x = x0; x <= x1; x++) {
    const trim = y === 188 || (x - x0) % 20 === 0, scuff = y > base - 4 && hash(x, y, 8) < 0.3;
    put(x, y, trim ? '#7a9ac8' : scuff ? '#2a3a5a' : x < x0 + 2 ? '#4a6aa8' : '#34508a');
  }
  for (let x = x0 - 1; x <= x1 + 1; x++) put(x, base, '#120c18');
  // the awning: a striped tarp on two poles, its scalloped edge
  for (let x = AWN.x0; x <= AWN.x1; x++) {
    const u = (x - AWN.x0) / (AWN.x1 - AWN.x0), yb = Math.round(lerp(AWN.y0 + 1, AWN.y1, u));
    for (let y = Math.round(lerp(AWN.y0 - 3, AWN.y1 - 5, u)); y <= yb; y++) put(x, y, Math.floor((x - AWN.x0) / 4) % 2 ? '#2a5aa8' : '#e8e4dc');
    if (x % 4 === 1) put(x, yb + 1, Math.floor((x - AWN.x0) / 4) % 2 ? '#1e4488' : '#c8c4bc');
  }
  for (const px0 of [AWN.x1 - 2]) for (let y = AWN.y1 - 2; y <= base; y++) { put(px0, y, '#8a8a98'); put(px0 + 1, y, '#3a3a48'); }
  // the fishball cart: a wooden box on two wheels, a glass case, the wok of oil, a pot of sauce, skewers in a cup
  for (let y = CART.top; y < CART.base - 3; y++) for (let x = CART.x0; x <= CART.x1; x++)
    put(x, y, y === CART.top ? '#c8905a' : x === CART.x0 ? '#a87a4a' : y < CART.top + 3 ? '#8a5a34' : (x - CART.x0) % 6 === 0 ? '#4a2a1c' : '#6a4228');
  for (let y = CART.top - 5; y < CART.top; y++) for (let x = CART.x0 + 2; x <= CART.x0 + 10; x++)
    put(x, y, x === CART.x0 + 2 || x === CART.x0 + 10 || y === CART.top - 5 ? '#c8d8e0' : y > CART.top - 3 ? '#e8b060' : '#f4d8a0');   // the glass case of fishballs
  for (let x = CART.x0 + 12; x <= CART.x1 - 1; x++) { put(x, CART.top - 1, '#1a1418'); put(x, CART.top - 2, x === CART.x0 + 12 || x === CART.x1 - 1 ? '#1a1418' : '#6a4a20'); }   // the wok
  for (let k = 0; k < 4; k++) put(CART.x1 - 3 + (k & 1), CART.top - 4 - k, '#d8c8a0');                                                  // skewers
  for (const wx of [CART.x0 + 4, CART.x1 - 4]) for (let j = -2; j <= 2; j++) for (let i = -2; i <= 2; i++) {
    const d = Math.hypot(i, j); if (d <= 2.6) put(wx + i, CART.base - 2 + j, d > 1.6 ? '#1a1418' : d > 0.6 ? '#4a4a58' : '#8a8a98');
  }
  // plastic stools
  for (const S of STOOLS) {
    for (let x = S.x - 3; x <= S.x + 3; x++) { put(x, base - 7, mixHex(S.col, '#ffffff', 0.15)); put(x, base - 6, S.col); }
    for (let y = base - 5; y < base; y++) { put(S.x - 3 + ((y - base + 5) >> 2), y, mixHex(S.col, '#000000', 0.3)); put(S.x + 3 - ((y - base + 5) >> 2), y, mixHex(S.col, '#000000', 0.45)); }
  }
  const c = p.done();
  sign(p.x, 'MERIENDA', Math.round((x0 + x1 - signWidth('MERIENDA')) / 2), eave + 3, '#f6e8c8');
  return c;
}
// a capiz parol: a five-pointed star with a ring of light round it and two tails, drawn lit
function renderParol(col) {
  const s = 15, p = painter(s, 22), c = 7, put = p.put;
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    const dx = x - c, dy = y - c, a = Math.atan2(dy, dx) + Math.PI / 2, r = Math.hypot(dx, dy);
    const star = 3.2 + 3.4 * Math.pow(Math.max(0, Math.cos(2.5 * a)), 3);
    if (r <= star) put(x, y, r < 2 ? '#fffbe8' : r < star - 1.2 ? col : mixHex(col, '#5a2a2a', 0.35));
    else if (Math.abs(r - 6.6) < 0.5) put(x, y, mixHex(col, '#fff4d8', 0.3));
  }
  for (let y = s; y < 22; y++) { put(c - 2, y, y % 2 ? col : '#fff0d0'); put(c + 2, y, y % 2 ? '#fff0d0' : col); }
  put(c, 0, '#2a2030');
  return p.done();
}

/* ================= The kiosk's roof sign: the billboard ================= */
// a lightbox on two steel posts over the kiosk, two small lamps on goosenecks; the city's own ad is the kiosk's name:
// KAPE NI LOLA ("Grandma's coffee"), with a steaming cup
const BB_LAMPS = [BB.x + 12, BB.x + BB.w - 13];
function renderBillboard() {
  const p = painter(W, H), put = p.put, X0 = BB.x - 2, X1 = BB.x + BB.w + 1, Y0 = BB.y - 2, Y1 = BB.y + BB.h + 1;
  for (const cx of [BB.x + 9, BB.x + BB.w - 10]) for (let y = Y1 + 1; y < KIOSK.eave - 6; y++) { put(cx, y, '#4a4252'); put(cx + 1, y, '#1a1420'); if ((y - Y1) % 6 === 3) { put(cx - 1, y, '#2a2230'); put(cx + 2, y, '#2a2230'); } }
  bres(put, BB.x + 10, Y1 + 2, BB.x + BB.w - 11, KIOSK.eave - 8, '#2a2230');
  bres(put, BB.x + BB.w - 11, Y1 + 2, BB.x + 10, KIOSK.eave - 8, '#2a2230');
  for (let x = X0; x <= X1; x++) { put(x, Y0, '#14101c'); put(x, Y1, '#14101c'); put(x, Y0 + 1, '#3a3446'); put(x, Y1 - 1, '#4a2a2a'); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, '#14101c'); put(X1, y, '#14101c'); put(X0 + 1, y, '#3a3446'); put(X1 - 1, y, '#1a1420'); }
  for (const lx of BB_LAMPS) {
    for (let y = Y0 - 4; y < Y0; y++) put(lx, y, '#3a3446');
    for (let i = lx - 2; i <= lx + 2; i++) put(i, Y0 - 5, i === lx - 2 ? '#5a5466' : '#2a2434');
  }
  return p.done();
}
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const lamp = Math.min(...BB_LAMPS.map(lx => Math.abs(BB.x + x - lx))) / 12;
    put(x, y, ramp(['#8a2e22', '#74241e', '#5e1c1a', '#481416'], clamp(y / h * 0.75 + lamp * 0.2, 0, 1), x, y));
  }
  for (let x = 1; x < w - 1; x++) { put(x, 1, '#e8b860'); put(x, h - 2, '#b8803a'); }
  for (let y = 1; y < h - 1; y++) { put(1, y, '#e8b860'); put(w - 2, y, '#b8803a'); }
  // the cup of coffee with its saucer and a curl of steam
  const CUP = ['.s.s...', 's.s....', '.s.s...', 'wwwww..', 'wcccwhh', 'wcccw.h', 'wwwwwhh', '.www...', 'WWWWWW.'];
  CUP.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const k = row[i]; if (k !== '.') put(40 + i, 3 + j, k === 's' ? '#e8d8c8' : k === 'c' ? '#5a2e1a' : k === 'W' ? '#d8c8b0' : '#f4ead8'); } });
  const c = p.done();
  const [g, gx] = makeCanvas(w, h);                                    // KAPE at twice the size, in cream
  const BIG = { K: WIDE.K, A: ['0110', '1001', '1111', '1001', '1001'], P: ['1110', '1001', '1110', '1000', '1000'], E: FONT3.E };
  [...'KAPE'].forEach((ch, k) => glyph(gx, BIG[ch], k * 5, 0, '#ffe8b8'));
  p.x.imageSmoothingEnabled = false;
  p.x.drawImage(g, 0, 0, 18, 5, 3, 2, 36, 10);
  sign(p.x, 'NI LOLA', 5, 13, '#ffc860');
  return gradeAd(c, { level: 0.9, tint: '#1a1030', mix: 0.08, wear: 0.15, seed: 23 });
}

/* ================= People ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#e8b890', '#d09a70', '#b07a52', '#8a5a3c'], HAIRS = ['#141012', '#2a1a14', '#4a2a1a', '#6a3a22', '#8a8288'];
const PNIGHT = '#1a1232', RIM = '#ffb070';
// a look for a person h px tall: proportions and colours dimmed for the dusk, the afterglow catching the shoulders
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, PNIGHT, dk);
  const hh = h >= 20 ? 5 : 4, th = Math.round(h * 0.37);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 21 ? 7 : h >= 18 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], RIM, 0.35), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.28),
    topL: mixHex(top, RIM, 0.38), bot: m(bot), shoe: m('#1e1820'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, apron: o.apron ? m(o.apron) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * o.aL / o.aR: 0 down, 2 phone up, 3 out (pointing, offering, stirring), 5 hand to the face (eating)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
    }
  } else if (o.sit && side) {
    const tl = P.h >= 22 ? 7 : 5, sh = o.shin ?? 4;
    R(dir > 0 ? x - 1 : x - tl + 2, fy - 1, tl, 2, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 2, fy + 1, 2, sh, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 1, fy + 1 + sh, 3, 1, P.shoe);
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                                                  // the afterglow on the shoulders
  if (P.apron && view !== 0) R(side ? (dir > 0 ? x : x - 1) : tx + 1, ty + 2, side ? 2 : bw - 2, th + 2, P.apron);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#ff9a5a'); } }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 5) { R(x + dir, ty, 1, 2, P.topD); R(x + dir * 2, hy + hh - 2, 1, 2, P.skin); }
    else { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}
const TOPS = ['#d8484a', '#e8c040', '#ece6d8', '#3a6ac8', '#2aa08a', '#e07aa8', '#e8843a', '#4a9a4a', '#8a5ac8', '#c8d0e0', '#9a2a3a', '#5ab0d8'];
const BOTS = ['#33466e', '#22202c', '#a8926a', '#d0ccc4', '#3a3a58', '#4a3a30'];

/* ================= The loop's timeline (seconds) =================
   t          what
   6-30       a plane's landing lights come in low over the bay towards the airport (again 54, 102, 150, 198)
   18-36      a line of egrets flies home across the afterglow, left to right (again 138-156, right to left)
   20-80      the taho vendor walks in along the promenade, stops at the couple on the wall (46-60), ladles two cups of
              taho, they eat; he walks on, calling (sound: the crowd bed)
   24-92      the fast ferry to Bataan leaves Harbour Square (left), horn at 26, and crosses the bay towards Orion
   62-78      the cat crosses the promenade; the pigeons flutter up as it passes (back again 202-218)
   96-118     the Baywalk lamp posts run their colour chase, panel by panel, then settle
   100-142    the fruit bats leave their roost behind the city: a few, then a stream over the bay, then stragglers
   152-196    a fishing bangka putters in close along the seawall with its lamp; the fisherman on the wall waves
   176-183    Malate Church's bell behind us, three strokes
   200-228    a bite: the rod dips (200), he reels in (204), a fish flips out (206, splash), the kids run over to see
              (206-222) and he shows it before it goes in the bucket (216); the kids go back to the wall
   all loop   the afterglow deepens and comes back once a loop; glints on the bay; ships' anchor lights, the refinery
              flare, the cranes' beacons; walkers, a jogger, the balloon seller; the guitarist plays and his friends
              sway; the kiosk serves coffee every 24 s; the cook stirs; drips ring the puddles; the boulevard's
              headlights sweep the wet promenade behind us                                                         */
const PLANES = [6, 54, 102, 150, 198], PLANE_DUR = 24;
const EGRETS = [{ t0: 18, t1: 36, dir: 1 }, { t0: 138, t1: 156, dir: -1 }];
const TAHO = { in: 20, stop: 46, go: 60, out: 80 };
const FERRY = { t0: 24, t1: 92 };
const CAT_RUNS = [{ t0: 62, t1: 78, x0: 44, x1: 438 }, { t0: 202, t1: 218, x0: 438, x1: 44 }];
const CHASE = { t0: 96, t1: 118 };
const BATS = { t0: 100, t1: 142, n: 46, fly: 14 };
const BANGKA = { t0: 152, t1: 196, wl: 184 };
const BELLS = [176, 179.5, 183];
const CATCH = { bite: 200, reel: 204, out: 206, show: 208, bucket: 216, back: 222, done: 228 };
const glowBreath = t => 0.78 + 0.22 * Math.cos(TAU * t / LOOP);          // the afterglow deepens once a loop and returns

/* ================= The cast: who is on the Baywalk at this hour =================
   rank: drawn while rank < crowd (Settings, People); rank 0 at every level above 0 (the vendors, the fisherman, the
   guitarist, the kids of the catch); couples share one rank */
const rk = i => frac((i + 0.5) * 0.618);
const CAST = [
  { id: 'vendor', x: 70, y: KIOSK.base - 7, P: look(21, 1, 0, '#e8e0d0', '#2a2a34', { apron: '#c84a3a', dk: 0.12 }), rank: 0 },
  { id: 'counter', x: 100, y: KIOSK.base + 1, P: look(21, 2, 1, TOPS[3], BOTS[2], { dk: 0.22 }), rank: rk(2) },
  { id: 'stool', x: STOOLS[0].x, y: KIOSK.base - 7, P: look(21, 0, 2, TOPS[5], BOTS[0], { long: 4, dk: 0.2 }), rank: rk(3) },
  { id: 'cook', x: 143, y: KIOSK.base, P: look(21, 3, 0, '#5a6a4a', BOTS[1], { hat: '#2a2a34', dk: 0.2 }), rank: 0 },
  { id: 'coupleA', x: 186, y: WALL, P: look(21, 1, 1, TOPS[9], BOTS[0]), rank: rk(0) },
  { id: 'coupleB', x: 194, y: WALL, P: look(20, 0, 0, TOPS[0], BOTS[3], { long: 4 }), rank: rk(0) },
  { id: 'phone', x: 215, y: PROM, P: look(21, 0, 1, TOPS[1], BOTS[0], { long: 3 }), rank: rk(6) },
  { id: 'kidA', x: 234, y: PROM, P: look(15, 1, 0, TOPS[4], BOTS[0]), rank: 0, run: 254 },
  { id: 'kidB', x: 242, y: PROM, P: look(14, 2, 1, TOPS[6], BOTS[1], { long: 2 }), rank: 0, run: 278 },
  { id: 'fisher', x: 264, y: WALL, P: look(21, 3, 0, '#4a5a7a', BOTS[1], { hat: '#6a5a3a' }), rank: 0 },
  { id: 'guitar', x: 330, y: WALL, P: look(21, 2, 0, TOPS[10], BOTS[1]), rank: 0 },
  { id: 'friendA', x: 343, y: WALL, P: look(20, 0, 2, TOPS[11], BOTS[0], { long: 3 }), rank: rk(5) },
  { id: 'friendB', x: 351, y: WALL, P: look(21, 1, 1, TOPS[2], BOTS[4]), rank: rk(5) },
];
const serving = t => t >= TAHO.stop && t < TAHO.go;
const inCatch = t => t >= CATCH.out && t < CATCH.done;
function castPose(c, t) {
  switch (c.id) {
    case 'vendor': return frac(t / 24) < 0.22 ? { view: 2, dir: 1, aR: 3 } : { view: 1 };
    case 'counter': return { view: 0, aR: frac(t / 24) < 0.22 ? 3 : 0 };
    case 'stool': return { view: 2, dir: -1, sit: true, shin: 5, aR: frac(t / 8 + 0.2) < 0.35 ? 5 : 3 };
    case 'cook': return { view: 2, dir: -1, aR: step(t, 240) & 1 ? 3 : 0 };
    case 'coupleA':
      if (serving(t)) return { view: 2, dir: 1, sit: true, shin: 0, aR: t >= TAHO.stop + 8 ? 5 : 3 };
      if (t >= TAHO.go && t < TAHO.go + 20) return { view: 0, sit: true, aR: frac(t / 4) < 0.4 ? 3 : 0 };
      return { view: 0, sit: true, aR: frac(t / 40) < 0.2 ? 3 : 0, lean: frac(t / 40 + 0.5) < 0.4 ? 1 : 0 };
    case 'coupleB': return { view: 0, sit: true, lean: frac(t / 40 + 0.5) < 0.5 ? -1 : 0, aL: serving(t) && t >= TAHO.stop + 9 ? 5 : 0 };
    case 'phone': {
      const up = frac(t / 24) < 0.55 || (t >= FERRY.t0 && t < FERRY.t0 + 18) || (t >= BATS.t0 + 8 && t < BATS.t0 + 30);
      return { view: 0, aR: up ? 2 : 0, lean: frac(t / 30) < 0.15 ? 1 : 0 };
    }
    case 'kidA': case 'kidB': {
      const run = kidRun(c, t);
      if (run) return run;
      return c.id === 'kidA' ? { view: 0, aR: frac(t / 16) < 0.45 ? 3 : 0 } : { view: 0, lean: frac(t / 12) < 0.3 ? 1 : 0 };
    }
    case 'fisher': {
      if (t >= CATCH.show && t < CATCH.bucket) return { view: 2, dir: -1, sit: true, shin: 0, aR: 3 };
      return { view: 0, sit: true, aR: 3 };
    }
    case 'guitar': return { view: 2, dir: 1, sit: true, shin: 0 };
    case 'friendA': return { view: 0, sit: true, lean: (step(t, 60) & 1) ? -1 : 0 };
    case 'friendB': return { view: 0, sit: true, lean: (step(t, 60) & 1) ? 0 : 1, aR: t >= 228 && t < 238 ? 3 : 0 };
  }
  return {};
}
// the kids run over to the fisherman when he lands a fish, and walk back after
function kidRun(c, t) {
  if (t < CATCH.out || t >= CATCH.done) return null;
  const go = smooth(CATCH.out, CATCH.out + 4, t), back = smooth(CATCH.back, CATCH.done, t), u = go - back;
  const x = Math.round(lerp(c.x, c.run, u)), moving = (t < CATCH.out + 4) || (t >= CATCH.back);
  return { x, view: moving ? 2 : 0, dir: t < CATCH.back ? 1 : -1, step: moving ? step(t, 1440) & 1 : 0, aR: !moving && c.id === 'kidA' ? 3 : 0 };
}
// the fishing rod's tip: still with a small bob, dipping at the bite, raised as he reels in, held up to show the fish
function rodTip(c, t) {
  const bob = Math.round(wave(t, 48, 0.2) * 0.6);
  if (t >= CATCH.bite && t < CATCH.reel) return [c.x + 27, c.y - 23 + (step(t, 960) & 1) * 2];
  if (t >= CATCH.reel && t < CATCH.show) return [c.x + 22, c.y - 33];
  if (t >= CATCH.show && t < CATCH.bucket) return [c.x - 8, c.y - 32];
  return [c.x + 27, c.y - 29 + bob];
}
function drawFish(x, y, t) {
  const f = step(t, 1440) & 1;
  R(x - 1, y, 3, 2, '#c8d0e0'); R(x - 1, y, 3, 1, '#eef2ff'); R(x - 2 + f * 4, y + 1, 1, 1, '#9aa4b8'); R(x, y + 2, 1, 1, '#8a94a8');
}
function drawCastMember(c, t) {
  const o = castPose(c, t), x = o.x ?? c.x;
  if (c.id === 'vendor') {                                               // behind the counter, seen to the waist
    ctx.save(); ctx.beginPath(); ctx.rect(KIOSK.x0 + 2, KIOSK.eave + 9, KIOSK.x1 - KIOSK.x0 - 3, 185 - KIOSK.eave - 9); ctx.clip();
    const hy = person(x, c.y, c.P, o); ctx.restore();
    if (o.view === 2) { R(x + 5, 182, 3, 3, '#f4ead8'); R(x + 6, 181, 1, 1, '#e8e0d0'); }   // a cup of coffee over the counter
    return hy;
  }
  const hy = person(x, c.y, c.P, o);
  if (c.id === 'fisher') {                                               // the rod out over the water, the line down into it
    const [tx, ty] = rodTip(c, t), bx = o.view === 2 ? c.x - 3 : c.x + 4, by = c.y - 10;
    ctx.fillStyle = '#2a2026';
    const n = Math.max(Math.abs(tx - bx), Math.abs(ty - by));
    for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(lerp(bx, tx, k / n)), Math.round(lerp(by, ty, k / n)), 1, 1);
    ctx.globalAlpha = 0.55; ctx.fillStyle = '#e0d0c8';
    if (t >= CATCH.out && t < CATCH.bucket) {                            // the fish on the line
      const fy = t < CATCH.show ? Math.round(lerp(c.y + 2, ty + 8, smooth(CATCH.out, CATCH.out + 1.5, t))) : ty + 8;
      for (let y = ty + 1; y < fy; y += 2) ctx.fillRect(tx, y, 1, 1);
      ctx.globalAlpha = 1; drawFish(tx, fy, t);
    } else for (let y = ty + 1; y < c.y - 4; y += 2) ctx.fillRect(tx, y, 1, 1);
    ctx.globalAlpha = 1;
    R(c.x - 9, c.y - 4, 5, 4, '#5a6a8a'); R(c.x - 9, c.y - 4, 5, 1, '#8a9ab8');   // his bucket on the wall
  }
  if (c.id === 'guitar') {                                               // the guitar across his lap, strumming
    const s = step(t, 1920) & 1;
    R(c.x + 1, c.y - 8, 6, 5, '#c88a4a'); R(c.x + 2, c.y - 8, 4, 1, '#e8b070'); R(c.x + 3, c.y - 7, 2, 2, '#2a1a14');
    for (let k = 0; k < 7; k++) R(c.x - 1 - k, c.y - 8 - (k >> 1), 1, 1, '#6a4a2a');
    R(c.x + 2 + s, c.y - 9 + s, 1, 2, c.P.skin);
  }
  if ((c.id === 'coupleA' || c.id === 'coupleB') && t >= TAHO.stop + 8 && t < TAHO.go + 14 && c.rank < 1) {   // cups of taho
    const cx = c.id === 'coupleA' ? x + 3 : x - 4;
    R(cx, hy + 8, 2, 2, '#f0e0c0'); R(cx, hy + 8, 2, 1, '#c88a4a');
  }
  return hy;
}

/* ================= Walkers on the promenade, each mirrored on the wet pavers ================= */
// they cross a whole number of times a loop; the taho vendor walks in once, on his own script (TAHO)
const WALKERS = [
  { id: 'balloon', y: FEET + 1, k: 3, ph: 0.15, dir: 1, pace: [168, 462], P: look(22, 1, 0, '#c8b890', BOTS[2], { hat: '#e8e0c8', dk: 0.25 }), rank: 0 },
  { id: 'jog', y: FEET + 2, k: 8, ph: 0.55, dir: -1, jog: true, P: look(22, 1, 1, TOPS[7], BOTS[1], { dk: 0.25 }), rank: rk(8) },
  { id: 'stroll', y: FEET, k: 4, ph: 0.72, dir: -1, P: look(21, 0, 2, TOPS[5], BOTS[0], { long: 4, dk: 0.25 }), mate: look(22, 2, 0, TOPS[9], BOTS[1], { dk: 0.25 }), rank: rk(11) },
  { id: 'dog', y: FEET + 2, k: 4, ph: 0.35, dir: 1, P: look(22, 3, 4, '#5a6a4a', BOTS[2], { hat: '#3a3a44', dk: 0.25 }), dog: '#c89a5a', rank: rk(10) },
  { id: 'mother', y: FEET + 1, k: 3, ph: 0.9, dir: 1, P: look(21, 1, 1, TOPS[8], BOTS[4], { long: 4, dk: 0.25 }), kid: look(13, 1, 0, TOPS[1], BOTS[0], { dk: 0.25 }), rank: rk(7) },
  { id: 'jog2', y: FEET + 1, k: 8, ph: 0.05, dir: 1, jog: true, P: look(22, 0, 1, TOPS[11], BOTS[1], { long: 3, dk: 0.25 }), rank: rk(1) },
];
const TAHO_P = look(22, 2, 0, '#e8e4d8', BOTS[1], { hat: '#c8b890', dk: 0.25 });
// the balloon seller strolls back and forth along the open stretch (his balloons never cross the kiosk's signs)
const walkerX = (w, t) => {
  const u = frac(w.k * t / LOOP + w.ph);
  if (w.pace) return lerp(w.pace[0], w.pace[1], u < 0.5 ? u * 2 : 2 - u * 2);
  return w.dir > 0 ? lerp(-26, W + 26, u) : lerp(W + 26, -26, u);
};
const walkerDir = (w, t) => w.pace ? (frac(w.k * t / LOOP + w.ph) < 0.5 ? 1 : -1) : w.dir;
function tahoAt(t) {
  if (t < TAHO.in || t >= TAHO.out) return null;
  if (t < TAHO.stop) return { x: lerp(W + 16, 206, (t - TAHO.in) / (TAHO.stop - TAHO.in)), walk: true };
  if (t < TAHO.go) return { x: 206, walk: false };
  return { x: lerp(206, -20, (t - TAHO.go) / (TAHO.out - TAHO.go)), walk: true };
}
const BALLOONS = [[-3, -16, '#e84a5a'], [2, -18, '#4a9ae8'], [6, -15, '#f0d040'], [-1, -21, '#5ad08a'], [4, -23, '#e87ad0'], [-5, -19, '#ff9a3a'], [8, -20, '#f4f0e8']];
// one walker's body and what they carry (drawn twice: as they are, and mirrored in the wet pavers)
function walkerBody(w, x, y, t, mirror = false) {
  const st = (step(t, w.jog ? 1440 : 768) + (w.k * 3)) & 1, dir = walkerDir(w, t);
  if (w.dog) {                                                     // a small dog trotting ahead on its lead
    const dx = Math.round(x + dir * 9), dy = y;
    R(dx - 3, dy - 4, 6, 2, w.dog); R(dx + (dir > 0 ? 2 : -4), dy - 6, 3, 3, w.dog); R(dx + (dir > 0 ? 4 : -5), dy - 5, 1, 1, '#1a1418');
    R(dx - 3, dy - 2, 1, 2, w.dog); R(dx + 2, dy - 2, 1, 2, w.dog); if (st) { R(dx - 2, dy - 1, 1, 1, w.dog); R(dx + 1, dy - 1, 1, 1, w.dog); }
    R(dir > 0 ? dx - 4 : dx + 3, dy - 5, 1, 1, w.dog);
    for (let k = 1; k < 6; k++) R(Math.round(x + dir * (2 + k)), Math.round(y - 11 + k * 1.2), 1, 1, '#3a2a26');
  }
  if (w.kid) person(x + dir * 6, y, w.kid, { view: 2, dir, step: st ^ 1 });
  const hy = person(x, y, w.P, { view: 2, dir, step: st, aR: w.kid ? 3 : 0 });
  if (w.mate) person(x - dir * 7, y, w.mate, { view: 2, dir, step: st ^ 1 });
  if (w.id === 'balloon' && !mirror) {                             // a bunch of balloons on strings over his shoulder
    const bx = x + dir * 2, by = hy - 2;
    for (const [i, j, col] of BALLOONS) {
      const sx = bx + i + Math.round(wave(t, 30, (i + j) * 0.05) * 0.6), sy = by + j;
      R(sx, sy, 3, 3, col); R(sx + 1, sy - 1, 1, 1, col); R(sx, sy, 1, 1, mixHex(col, '#ffffff', 0.5));
      ctx.globalAlpha = 0.5; for (let k = sy + 3; k < by + 4; k += 2) R(Math.round(lerp(sx + 1, bx, (k - sy) / (by + 4 - sy))), k, 1, 1, '#c8c0c8'); ctx.globalAlpha = 1;
    }
  }
  return hy;
}
function tahoBody(x, y, t, walk) {
  const st = walk ? step(t, 768) & 1 : 0, serve = !walk;
  const hy = person(x, y, TAHO_P, { view: 2, dir: -1, step: st, aR: serve && frac(t / 3) < 0.5 ? 3 : 0 });
  const py = hy + TAHO_P.hh + 1;                                   // the yoke over his shoulder, a can at each end
  if (walk) for (let i = -11; i <= 11; i++) R(x + i, py + (Math.abs(i) > 8 ? 1 : 0) + (st && Math.abs(i) > 8 ? 1 : 0), 1, 1, '#8a6a3a');
  for (const sgn of [-1, 1]) {
    const cx = walk ? x + sgn * 11 : x + sgn * 6, cy = walk ? py + 4 + st : y - 6;
    if (walk) R(cx, py + 1, 1, 3, '#5a4a3a');
    for (let j = 0; j < 7; j++) R(cx - 3, cy + j, 7, 1, j === 0 ? '#b8b4c4' : j === 1 ? '#5a5668' : j === 6 ? '#3a3648' : (j & 1) ? '#7a7688' : '#6a6678');
    R(cx - 3, cy + 1, 1, 6, '#9a96a8'); R(cx + 3, cy + 1, 1, 6, '#3a3648');
  }
  if (serve) { R(x - 5, hy + 7, 2, 2, '#f0e0c0'); R(x - 5, hy + 7, 2, 1, '#c88a4a'); }   // a cup of taho, the brown syrup on top
}
// everyone walking, nearest last, with their reflections on the wet promenade
function drawWalkers(t, crowd) {
  const list = [];
  for (const w of WALKERS) if (w.rank < crowd) list.push({ y: w.y, draw: m => walkerBody(w, Math.round(walkerX(w, t)), w.y, t, m) });
  const th = crowd > 0 ? tahoAt(t) : null;
  if (th) list.push({ y: FEET + 1, draw: () => tahoBody(Math.round(th.x), FEET + 1, t, th.walk) });
  list.sort((a, b) => a.y - b.y);
  ctx.save(); ctx.beginPath(); ctx.rect(0, FEET + 1, W, 40); ctx.clip();
  for (const L of list) { ctx.save(); ctx.globalAlpha = 0.3; ctx.translate(0, 2 * L.y + 1); ctx.scale(1, -1); L.draw(true); ctx.restore(); }
  ctx.restore();
  for (const L of list) L.draw(false);
}

/* ================= The near promenade: a cat on its rounds, pigeons, drips, headlights from the boulevard ================= */
const CAT_Y = 240;
function catAt(t) {
  const r = CAT_RUNS.find(c => t >= c.t0 && t < c.t1);
  if (r) return { x: lerp(r.x0, r.x1, (t - r.t0) / (r.t1 - r.t0)), dir: Math.sign(r.x1 - r.x0), walk: true };
  return { x: t >= CAT_RUNS[0].t1 && t < CAT_RUNS[1].t0 ? CAT_RUNS[0].x1 : CAT_RUNS[0].x0, dir: t >= CAT_RUNS[0].t1 && t < CAT_RUNS[1].t0 ? -1 : 1, walk: false };
}
function drawCat(t) {
  const c = catAt(t), x = Math.round(c.x), y = CAT_Y, d = c.dir, st = step(t, 1440) & 1, B = '#a8622e', L = '#e09a52';
  if (c.walk) {
    R(x - 4, y - 5, 9, 3, B); R(x - 4, y - 5, 9, 1, L);
    R(x + d * 4 - (d < 0 ? 1 : 0), y - 7, 3, 3, B); R(x + d * 5, y - 8, 1, 1, B); R(x + d * 4 - (d < 0 ? 0 : -1) * 0, y - 8, 1, 1, B);
    R(x + d * 5 + (d > 0 ? 1 : -1), y - 6, 1, 1, '#c8d878');
    for (const lx of [x - 3, x + 3]) R(lx + (st ? d : 0), y - 2, 1, 2, B);
    R(x - d * 5, y - 7, 1, 3, B); R(x - d * 6, y - 8, 1, 1, B);
  } else {                                                        // sitting, washing a paw now and then
    R(x - 2, y - 7, 5, 7, B); R(x - 2, y - 7, 5, 1, L); R(x - 1, y - 10, 4, 3, B); R(x - 1, y - 11, 1, 1, B); R(x + 2, y - 11, 1, 1, B);
    R(x, y - 9, 1, 1, '#c8d878'); R(x + 2, y - 9, 1, 1, '#c8d878');
    R(x + 3, y - 1, 3, 1, B); R(x + 5, y - 2, 1, 1, B);
    if (frac(t / 10) < 0.3) R(x + 1, y - 9 + (step(t, 960) & 1), 1, 2, L);
  }
}
const PIGEONS = [[206, 0.1], [218, 0.45], [232, 0.8], [244, 0.3]];
function drawPigeons(t) {
  const cat = catAt(t);
  PIGEONS.forEach(([px0, ph]) => {
    const away = cat.walk && Math.abs(cat.x - px0) < 24, peck = frac(t / 3 + ph) < 0.3, x = px0 + (away ? Math.round((px0 - cat.x) * 0.3) : 0);
    const y = 254 - (away ? 4 + (step(t, 1200) & 1) * 2 : 0);
    R(x - 2, y - 3, 5, 2, '#6a6e80'); R(x - 1, y - 4, 3, 1, '#8a8ea0'); R(x - 3, y - 2, 1, 1, '#4a4e5c');
    if (away) { R(x - 4, y - 5 + (step(t, 1200) & 1), 2, 1, '#9a9eb0'); R(x + 3, y - 5 + (step(t, 1200) & 1), 2, 1, '#9a9eb0'); }
    const hx = x + 3, hy = peck && !away ? y - 2 : y - 5;
    R(hx, hy, 2, 2, '#5a6278'); R(hx, hy + 1, 1, 1, '#4a8a7a'); R(hx + 2, hy + 1, 1, 1, '#d8a050');
    if (!away) { R(x - 1, y - 1, 1, 1, '#c86a5a'); R(x + 1, y - 1, 1, 1, '#c86a5a'); }
  });
}
// drips from the palms and the awning ring the puddles
const DRIPS = Array.from({ length: 22 }, (_, i) => ({ n: 40 + Math.floor(hash(i, 1, 61) * 30), ph: hash(i, 2, 61), P: PUDDLES[i % PUDDLES.length], s: i }));
function drawDrips(t) {
  ctx.fillStyle = '#9a90c8';
  for (const d of DRIPS) {
    const { cyc, u } = cycle(t, d.n, d.ph);
    if (u > 0.4) continue;
    const P = d.P, x = Math.round(P.x + (hash(d.s, cyc, 62) - 0.5) * P.rx * 1.3), y = Math.round(P.y + (hash(d.s, cyc, 63) - 0.5) * P.ry);
    if (!puddleAt(x, y)) continue;
    const r = 1 + Math.round(u * 14);
    ctx.globalAlpha = 0.6 * (1 - u / 0.4);
    ctx.fillRect(x - r, y, 1, 1); ctx.fillRect(x + r, y, 1, 1);
    if (r > 2) { ctx.fillRect(x - r + 1, y - 1, 2 * r - 1, 1); ctx.fillRect(x - r + 1, y + 1, 2 * r - 1, 1); }
  }
  ctx.globalAlpha = 1;
}
// a vehicle on Roxas Boulevard behind us sweeps its headlights over the near promenade now and then
const SWEEPS = Array.from({ length: 12 }, (_, i) => ({ t0: i * 20 + 3 + hash(i, 4, 7) * 8, dur: 5 + hash(i, 5, 7) * 2, dir: i & 1 ? 1 : -1, col: ['#ffe8c0', '#ffd0e8', '#d8f0ff'][i % 3] }));
function drawSweeps(t) {
  ctx.save(); ctx.beginPath(); ctx.rect(0, 214, W, H - 214); ctx.clip();
  for (const S of SWEEPS) {
    if (t < S.t0 || t >= S.t0 + S.dur) continue;
    const u = (t - S.t0) / S.dur, x = S.dir > 0 ? lerp(-60, W + 60, u) : lerp(W + 60, -60, u), a = Math.sin(Math.PI * u) * 0.9;
    halo(HL.sweep, x, 266, a); halo(HL.sweepCore, x, 262, a * 0.8);
  }
  ctx.restore();
}

/* ================= What crosses the sky and the bay ================= */
// glints of the afterglow on the bay's small waves, denser towards the horizon and under the glow
const SPARK = Array.from({ length: 320 }, (_, i) => ({ n: 150 + Math.floor(hash(i, 1, 91) * 90), ph: hash(i, 2, 91), s: i }));
function drawSparkles(t, gb) {
  for (const sp of SPARK) {
    const { cyc, u } = cycle(t, sp.n, sp.ph);
    if (u > 0.55) continue;
    const y = HZ + 1 + Math.floor(Math.pow(hash(sp.s, cyc, 92), 1.5) * (WALL - HZ - 2)), x = Math.floor(hash(sp.s, cyc, 93) * W);
    const d = (y - HZ) / (WALL - HZ), g = glowAt(x, HZ), len = 1 + Math.floor(hash(sp.s, cyc, 94) * (2 + d * 5));
    ctx.globalAlpha = (1 - Math.abs(u - 0.27) / 0.28) * (0.3 + g * 0.6 * gb);
    ctx.fillStyle = g > 0.5 ? '#ffd890' : g > 0.25 ? '#ff9a6a' : '#b880a8';
    ctx.fillRect(x, y, len, 1);
  }
  ctx.globalAlpha = 1;
}
// the fast ferry to Bataan: white, two hulls, a row of lit windows, the bridge on top
const FERRY_ROWS = ['...........kkkkkkk..........', '..........kyyyyyyk..........', '....hhhhhhhhhhhhhhhhhhh.....',
  '...hWWpWWpWWpWWpWWpWWhhh....', '..hhhhhhhhhhhhhhhhhhhhhhhhh.', '..RRRRRRRRRRRRRRRRRRRRRRRRR.', '...nnnn..............nnnn...'];
const FERRY_KEY = { k: '#2a2e3a', y: '#ffe2a0', h: '#c8c4d4', W: '#ffd890', p: '#5a5868', R: '#2a5aa8', n: '#1c2440' };
function ferryAt(t) {
  if (t < FERRY.t0 || t >= FERRY.t1) return null;
  const u = (t - FERRY.t0) / (FERRY.t1 - FERRY.t0);
  return { x: lerp(60, 492, u), wl: lerp(163, 147, u), s: u < 0.4 ? 1 : u < 0.72 ? 0.75 : 0.5 };
}
function drawFerry(t) {
  const f = ferryAt(t);
  if (!f) return;
  const w = Math.round(28 * f.s), h = Math.round(7 * f.s), x = Math.round(f.x - w / 2), y = Math.round(f.wl - h + 1);
  for (let k = 1; k < 30 * f.s; k++) {                                   // the wake fanning out behind it
    ctx.globalAlpha = (1 - k / (30 * f.s)) * 0.5;
    ctx.fillStyle = hash(k, step(t, 960), 3) < 0.6 ? '#e8d8d0' : '#b8a8b8';
    ctx.fillRect(x - k, Math.round(f.wl - Math.floor(k * 0.12)), 1, 1); ctx.fillRect(x - k, Math.round(f.wl + 1 + Math.floor(k * 0.12)), 1, 1);
  }
  ctx.globalAlpha = 0.3; ctx.drawImage(FERRY_C, 0, 3, 28, 4, x, f.wl + 1, w, Math.max(1, Math.round(4 * f.s))); ctx.globalAlpha = 1;
  ctx.drawImage(FERRY_C, x, y, w, h);
  halo(HL.deck, x + w * 0.5, y + h * 0.5, 0.8); R(x + w - 2, y + 1, 1, 1, '#4aff8a');
  streak(ctx, t, x + w / 2, Math.max(3, Math.round(10 * f.s)), '#ffd890', 0.25, 4, f.wl + 2, WALL);
}
// a fishing bangka: a narrow painted hull, bamboo outriggers on arched crossbars, a lamp at the bow, its fisherman
const BANGKA_P = look(13, 3, 0, '#c8484a', BOTS[1], { hat: '#c8b890', dk: 0.35 });
function bangkaAt(t) {
  if (t < BANGKA.t0 || t >= BANGKA.t1) return null;
  return { x: Math.round(lerp(W + 44, -50, (t - BANGKA.t0) / (BANGKA.t1 - BANGKA.t0))) };
}
function drawBangka(t) {
  const b = bangkaAt(t);
  if (!b) return;
  const x = b.x, wl = BANGKA.wl, bob = Math.round(wave(t, 120) * 0.6);
  // its wake behind it (to the right) and the mirror of its lamp
  for (let k = 1; k < 30; k++) { ctx.globalAlpha = (1 - k / 30) * 0.45; ctx.fillStyle = '#d8c8d0'; ctx.fillRect(x + 32 + k, wl + 1 + ((k + step(t, 960)) & 1), 1, 1); }
  ctx.globalAlpha = 1;
  streak(ctx, t, x + 2, 4, '#ffc870', 0.4, 6, wl + 2, WALL);
  const y = wl + bob;
  for (let i = -6; i <= 37; i++) R(x + i, y + 1, 1, 1, i % 3 ? '#a88a5a' : '#6a5a3a');          // the bamboo floats
  for (const cx of [x + 6, x + 25]) { R(cx - 4, y - 5, 9, 1, '#5a4a3a'); R(cx - 5, y - 4, 1, 4, '#5a4a3a'); R(cx + 5, y - 4, 1, 4, '#5a4a3a'); }   // crossbars
  for (let i = 0; i < 32; i++) {                                                              // the hull, prow and stern swept up
    const top = y - 3 - (i < 3 ? 3 - i : i > 28 ? i - 28 : 0);
    R(x + i, top, 1, y - top + 1, i < 3 || i > 28 ? '#e8e4d8' : '#d8d4c8');
    R(x + i, y - 1, 1, 1, '#2a6ac8'); R(x + i, y, 1, 1, '#c83a3a');
  }
  person(x + 26, y - 3, BANGKA_P, { view: 2, dir: -1, sit: true, shin: 2, aR: t >= BANGKA.t0 + 20 && t < BANGKA.t0 + 26 ? 3 : 0 });
  R(x + 2, y - 8, 1, 5, '#3a2a2a'); R(x + 1, y - 10, 3, 2, '#fff0c0'); halo(HL.cart, x + 2, y - 9, 0.9);
}
// fruit bats leaving their roost behind the city at dusk, streaming out over the bay
const BAT_LIST = Array.from({ length: BATS.n }, (_, i) => {
  const f = i / BATS.n, spread = f < 0.12 ? f / 0.12 * 0.15 : f < 0.85 ? 0.15 + (f - 0.12) / 0.73 * 0.7 : 0.85 + (f - 0.85) / 0.15 * 0.15;
  return { t0: BATS.t0 + spread * (BATS.t1 - BATS.t0 - BATS.fly), y: 18 + hash(i, 1, 51) * 52, amp: 2 + hash(i, 2, 51) * 4, k: 3 + hash(i, 3, 51) * 3, sp: 0.85 + hash(i, 4, 51) * 0.3, s: i };
});
function drawBats(t) {
  if (t < BATS.t0 || t >= BATS.t1) return;
  ctx.fillStyle = '#120c1a';
  for (const b of BAT_LIST) {
    const u = (t - b.t0) / (BATS.fly * b.sp);
    if (u < 0 || u >= 1) continue;
    const x = Math.round(lerp(W + 10, -12, u)), y = Math.round(b.y + u * 16 + Math.sin(u * b.k * TAU) * b.amp), up = (step(t, 1680) + b.s) & 1;
    ctx.fillRect(x - 1, y, 3, 1);
    if (up) { ctx.fillRect(x - 3, y - 1, 2, 1); ctx.fillRect(x + 2, y - 1, 2, 1); } else { ctx.fillRect(x - 3, y + 1, 2, 1); ctx.fillRect(x + 2, y + 1, 2, 1); }
  }
}
// egrets flying home low over the bay in a loose line
const EGRET_V = [[0, 0, 0], [-8, 3, 0.3], [-15, 5, 0.6], [-7, -3, 0.15], [-14, -5, 0.45]];
function drawEgrets(t) {
  const e = EGRETS.find(({ t0, t1 }) => t >= t0 && t < t1);
  if (!e) return;
  const u = (t - e.t0) / (e.t1 - e.t0), base = e.dir > 0 ? lerp(-24, W + 24, u) : lerp(W + 24, -24, u), fl = step(t, 960);
  EGRET_V.forEach(([dx, dy, ph], i) => {
    const x = Math.round(base + dx * e.dir), y = Math.round(98 + dy + 3 * Math.sin(TAU * (u * 2 + ph))), up = ((fl + i) >> 1) & 1;
    if (x > 30 && x < 100 && y > 108) return;
    ctx.fillStyle = '#f4ecec'; ctx.fillRect(x - 1, y, 4, 1); ctx.fillRect(x + (e.dir > 0 ? 3 : -2), y - 1, 1, 1);
    ctx.fillStyle = '#e0d4d8';
    if (up) { ctx.fillRect(x - 2, y - 1, 2, 1); ctx.fillRect(x + 1, y - 2, 2, 1); } else { ctx.fillRect(x - 2, y + 1, 2, 1); ctx.fillRect(x + 1, y + 1, 2, 1); }
    ctx.fillStyle = '#d8a050'; ctx.fillRect(x + (e.dir > 0 ? 4 : -3), y - 1, 1, 1);
  });
}
// a plane's landing lights coming in low over the bay towards the airport behind us to the south
function drawPlanes(t) {
  for (const p0 of PLANES) {
    if (t < p0 || t >= p0 + PLANE_DUR) continue;
    const u = (t - p0) / PLANE_DUR, x = Math.round(lerp(330, -14, u)), y = Math.round(lerp(58, 92, u)), a = smooth(0, 0.25, u) * (1 - smooth(0.9, 1, u));
    ctx.globalAlpha = a; R(x, y, 2, 1, '#fffaf0'); ctx.globalAlpha = 1;
    halo(HL.plane, x, y, a * (0.5 + 0.5 * u));
    if (frac(t * 0.8) < 0.15) R(x + 3, y - 1, 1, 1, '#ff4a3a');
  }
}

/* ================= Weather: Manila's own is a clear evening after a shower; a picked weather falls on the bay and the promenade ================= */
const WEATHER = weatherPlans([]);
const RAIN_LIGHTS = [{ x: 70, y: 178, r: 40 }, { x: LAMPS[0].x, y: LAMP_TOP + 6, r: 26 }, { x: LAMPS[1].x, y: LAMP_TOP + 6, r: 26 }];
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y - 4, r: 16 }];
const WX_LAND = [[HZ - 10, HZ + 8], [HZ + 8, WALL], [PROM + 2, H + 4]];
const WX = { land: WX_LAND, lights: RAIN_LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };
const WX_BB = { ...WX, lights: RAIN_LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };

function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.84 && (d - 0.84) * 3 > bayer(x, y)) p.put(x, y, '#05040e', 90);
  }
  return p.done();
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, STREAK_C, MARI_C, PORT_C, WATER_C, SHIP_C, WALL_C, PROM_C, PALM_C, LAMP_C, KIOSK_C, PAROL_C, BB_C, BB_AD, PITCH, VIG_C, FERRY_C;
function prerender() {
  HL = {
    moon: makeHalo(30, '#ffd8b0', 0.14, 1.8), venus: makeHalo(5, '#fff4e0', 0.4, 1.3),
    glow: makeHalo(150, '#ff7a30', 0.16, 1.4), glowCore: makeHalo(60, '#ffc060', 0.2, 1.5),
    shore: makeHalo(3, '#ffc070', 0.35, 1.2), flare: makeHalo(7, '#ff8a3a', 0.5, 1.3), crane: makeHalo(3, '#ff3a2a', 0.6, 1.2),
    green: makeHalo(3, '#4aff8a', 0.5, 1.2), ship: makeHalo(4, '#fff0d0', 0.4, 1.3), deck: makeHalo(7, '#ffd890', 0.3, 1.4),
    lamp: makeHalo(30, '#ffb060', 0.28, 1.6), lampCore: makeHalo(8, '#ffe0a0', 0.45, 1.4), panel: makeHalo(12, '#d8c8ff', 0.18, 1.4),
    kiosk: makeHalo(54, '#ffb060', 0.24, 1.5), cart: makeHalo(16, '#ffc070', 0.4, 1.4), parol: makeHalo(10, '#ffd8a0', 0.4, 1.4),
    bbLamp: makeHalo(6, '#ffd090', 0.3, 1.6), bbWash: makeHalo(34, '#ffb070', 0.07, 1.5), plane: makeHalo(8, '#fff4e0', 0.5, 1.3),
    sweep: makeHalo(52, '#ffe0b0', 0.15, 1.3), sweepCore: makeHalo(22, '#fff0d8', 0.12, 1.3),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); STREAK_C = STREAKS.map(renderStreak);
  MARI_C = renderMariveles(); PORT_C = renderPort(); WATER_C = renderWater();
  SHIP_C = SHIPS.map(renderShip);
  WALL_C = renderWall(); PROM_C = renderPromenade();
  PALM_C = Object.fromEntries(PALMS.map(P => [P.id, [-1, 0, 1].map(k => renderPalm(P, k * (P.near ? 0.06 : 0.045)))]));
  LAMP_C = LAMPS.map(renderLamp); KIOSK_C = renderKiosk(); PAROL_C = PAROLS.map(P => renderParol(P.col));
  BB_C = renderBillboard(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#ff4f00' });
  FERRY_C = sprite(FERRY_ROWS, FERRY_KEY);
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawShips(t, dist) {
  SHIPS.forEach((S, i) => {
    if (S.dist !== dist) return;
    const s = SHIP_C[i], bob = dist === 2 ? Math.round(0.4 * wave(t, 24 + i, i * 0.17)) : 0;
    ctx.globalAlpha = dist === 0 ? 0.25 : 0.35;                          // its mirror on the water, darker and broken
    for (let k = 0; k < Math.min(s.h, dist === 2 ? 10 : 4); k++) {
      const off = Math.round(Math.sin(TAU * 40 * t / LOOP + k * 0.9 + i) * (0.4 + k * 0.12));
      ctx.drawImage(s.c, 0, s.wl - k, s.w, 1, s.ox + off, S.wl + 1 + k + bob, s.w, 1);
    }
    ctx.globalAlpha = 1;
    ctx.drawImage(s.c, s.ox, s.oy + bob);
    for (const L of s.lights) {
      const tw = 0.7 + 0.3 * wave(t, 30 + (L.x % 17), L.y * 0.07);
      ctx.fillStyle = L.col; ctx.fillRect(L.x, L.y + bob, 1, 1);
      halo(dist === 2 && L.a < 0.9 ? HL.deck : HL.ship, L.x, L.y + bob, L.a * tw * (dist === 0 ? 0.6 : 1));
    }
    if (dist === 2) streak(ctx, t, S.x, Math.round(S.len * 0.35), '#ffd890', 0.22, i * 1.7, S.wl + 2, WALL);
  });
}
// the Baywalk lamps' colour chase: the panels run through the colours one after another, then settle
function drawChase(t) {
  const a = env(t, CHASE.t0, CHASE.t1, 3);
  if (a <= 0) return;
  const s = step(t, 960);
  LAMPS.forEach((L, li) => PANELS.forEach((_, k) => {
    const col = PANELS[(k + s + li) % 4];
    ctx.globalAlpha = a; R(L.x - 1, LAMP_TOP + 13 + k * 6, 3, 5, col); R(L.x - 1, LAMP_TOP + 13 + k * 6, 1, 5, mixHex(col, '#ffffff', 0.4));
  }));
  ctx.globalAlpha = 1;
  for (const L of LAMPS) halo(HL.panel, L.x, LAMP_TOP + 24, a);
}
function drawSteam(t) {
  for (let i = 0; i < 6; i++) {
    const { cyc, u } = cycle(t, 160, i / 6);
    const x = Math.round(CART.x1 - 6 + Math.sin(u * 5 + i) * 1.5 + hash(i, cyc, 3) * 3), y = Math.round(CART.top - 3 - u * 14);
    ctx.globalAlpha = (1 - u) * 0.45; R(x, y, 1, 1, '#e8e0e8');
  }
  ctx.globalAlpha = 1;
}
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over'; ctx.imageSmoothingEnabled = false;
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, gb = glowBreath(t);
  // the sky: stars, the evening star, the crescent, streaks of lit cloud, the afterglow breathing, bats, a plane
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#eef0ff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.3 + 0.7 * (0.5 + 0.5 * wave(t, s.k, s.ph)));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.45; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.venus, VENUS.x, VENUS.y, 0.8); R(VENUS.x, VENUS.y, 1, 1, '#fffaf0');
  halo(HL.moon, MOON.x, MOON.y + 2, 0.9);
  ctx.drawImage(MOON_C, MOON.x - MOON.r - 1, MOON.y - MOON.r - 1);
  for (const L of STREAK_C) drawScroll(L.c, L.y, L.k, t);
  halo(HL.glow, GLOW.x, HZ, 0.9 * gb); halo(HL.glowCore, GLOW.x, HZ - 2, 0.8 * gb);
  drawBats(t);
  drawPlanes(t);
  // Mariveles, the lights along the Bataan shore, the refinery's flare; the Yacht Club's masts and the port's cranes
  ctx.drawImage(MARI_C, 0, 0);
  for (const L of SHORE_LIGHTS) {
    ctx.globalAlpha = (L.dim ? 0.45 : 0.7) + 0.3 * wave(t, L.k, L.ph);
    ctx.fillStyle = L.warm ? '#ffc878' : '#f0f0ff'; ctx.fillRect(L.x, L.y, 1, 1);
  }
  ctx.globalAlpha = 1;
  const fl = 0.7 + 0.3 * hash(4, step(t, 1440), 2);
  R(FLARE.x, FLARE.y, 1, 2, '#ffb060'); R(FLARE.x, FLARE.y - 1, 1, 1, fl > 0.85 ? '#fff0c0' : '#ff9040'); halo(HL.flare, FLARE.x, FLARE.y, fl);
  ctx.drawImage(PORT_C, 0, 0);
  for (let x = 408; x < W; x += 7) { R(x, HZ - 3, 1, 1, '#ffc070'); halo(HL.shore, x, HZ - 3, 0.6 + 0.2 * wave(t, 24, x * 0.03)); }
  CRANES.forEach((C, i) => { const on = frac(t / 2 + i * 0.3) < 0.5; R(C.x, HZ - 4 - C.h, 1, 1, on ? '#ff4a3a' : '#8a2a2a'); if (on) halo(HL.crane, C.x, HZ - 4 - C.h, 0.9); });
  if (frac(t / 4) < 0.5) { R(MARINA_LIGHT.x, MARINA_LIGHT.y, 1, 1, '#6aff9a'); halo(HL.green, MARINA_LIGHT.x, MARINA_LIGHT.y, 0.8); }
  drawShips(t, 0);
  // the bay: its colour, the mirror of the glow and the mountain, the glitter, the glints, ships, the ferry, the bangka
  ctx.drawImage(WATER_C, 0, HZ);
  reflect(ctx, t, { top: HZ, rows: WALL - HZ, squash: 1.6, alpha: 0.36, amp: 0.45, k1: 40, k2: 96 });
  streak(ctx, t, GLOW.x, 30, '#ff9a40', 0.3 * gb, 2, HZ + 1, WALL);
  streak(ctx, t, GLOW.x, 12, '#ffd070', 0.45 * gb, 5, HZ + 1, WALL);
  streak(ctx, t, GLOW.x + 6, 6, '#fff0b0', 0.35 * gb, 9, HZ + 1, HZ + 30);
  drawSparkles(t, gb);
  drawShips(t, 1);
  drawFerry(t);
  drawShips(t, 2);
  drawBangka(t);
  drawEgrets(t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the promenade, the seawall, the palms, the kiosk and its sign, and everyone at the wall in feet order
  ctx.drawImage(PROM_C, 0, 0);
  drawDrips(t);
  drawSweeps(t);
  ctx.drawImage(WALL_C, 0, 0);
  const sw = id => { const v = wave(t, 40, id.length * 0.13); return PALM_C[id][v < -0.35 ? 0 : v > 0.35 ? 2 : 1]; };
  ctx.drawImage(sw('wallL'), 0, 0);
  ctx.drawImage(sw('wallR'), 0, 0);
  if (billboard) drawBillboard(t, pitch);
  ctx.drawImage(KIOSK_C, 0, 0);
  drawSteam(t);
  const DL = [];
  for (const c of CAST) if (c.rank < crowd) DL.push([c.y, () => drawCastMember(c, t)]);
  LAMPS.forEach((L, i) => DL.push([PROM, () => ctx.drawImage(LAMP_C[i], 0, 0)]));
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, draw] of DL) draw();
  drawChase(t);
  // light: the kiosk, the cart's lamp, the parols, the lamp posts
  halo(HL.kiosk, 70, 178, 0.9);
  const cf = 0.8 + 0.2 * hash(7, step(t, 1440), 3);
  R(CART.x0 + 6, CART.top - 9, 2, 3, '#fff0c0'); halo(HL.cart, CART.x0 + 7, CART.top - 8, cf);
  PAROLS.forEach((P, i) => { ctx.drawImage(PAROL_C[i], P.x - 7, P.y - 7 + Math.round(0.6 * wave(t, 20, i * 0.3))); halo(HL.parol, P.x, P.y, 0.75 + 0.2 * wave(t, 16, i * 0.4)); });
  for (const L of LAMPS) { halo(HL.lamp, L.x, LAMP_TOP + 5, 0.9); halo(HL.lampCore, L.x, LAMP_TOP + 4, 0.7); halo(HL.panel, L.x, LAMP_TOP + 24, 0.5); }
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the wet promenade mirrors the wall and its people; then the walkers with their own reflections, the cat, the pigeons
  reflect(ctx, t, { top: PROM + 1, rows: 30, squash: 1, alpha: 0.22, amp: 0.3, k1: 30, k2: 70 });
  for (const L of LAMPS) streak(ctx, t, L.x, 4, '#ffc880', 0.3, L.x * 0.1, PROM + 2, H);
  streak(ctx, t, 70, 14, '#ffb060', 0.26, 3, KIOSK.base + 2, H);
  drawWalkers(t, crowd);
  drawPigeons(t);
  drawCat(t);
  ctx.drawImage(sw('nearL'), 0, 0);
  ctx.drawImage(sw('nearR'), 0, 0);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 6, 0.8);
  ctx.drawImage(BB_C, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, BB.y - 7, 3, 1, '#f6e0b0'); halo(HL.bbLamp, lx, BB.y - 6, 0.9); }
}

const CROWD = 0.5;
export default {
  id: 'manila',
  name: 'Manila',
  tagline: 'Afterglow over Manila Bay',
  timeZone: 'Asia/Manila',
  accent: '#ff4f00',
  focusX: MARI.px,
  landmark: [Math.round(MARI.px - 2.8 * MARI.mh), Math.round(MARI.px + 4 * MARI.mh)],   // Mariveles from half height to half height
  landmarkTop: Math.round(HZ - MARI.mh),
  billboard: BB,
  crowd: CROWD,
  spots: [                                           // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: MARI.px - 70, y: HZ - MARI.mh - 2, w: 140, h: MARI.mh + 2 },   // Mount Mariveles' summit
    { kind: 'water', x: 198, y: 164, w: 56, h: 16 },                                      // the bay between the ships
    { kind: 'window', x: 48, y: KIOSK.eave + 9, w: 60, h: 185 - KIOSK.eave - 9 },          // the kiosk's lit counter
  ],
  ambience: { waves: 0.35, water: 0.3, traffic: 0.3, crowd: 0.22, wind: 0.15 },
  events: [
    { t: 26, type: 'horn', x: 90, gain: 0.45 },                                          // the Bataan ferry leaves
    ...BELLS.map(t => ({ t, type: 'bell', x: 240, gain: 0.35 })),                         // Malate Church, behind us
    { t: BANGKA.t0, type: 'boat', dur: BANGKA.t1 - BANGKA.t0, pan: [1, -1], gain: 0.5 },  // the bangka's little engine
    { t: CATCH.out, type: 'splash', x: 290, gain: 0.5 },                                  // the fish comes out of the water
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
